"use client";
import type { CareerStat } from "./types";
import type { IconTypes } from "@/components/icons/types";
import { Children, ReactNode, isValidElement, type JSX } from "react";
import CareerCards from ".";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import MonarchResolver from "@/monarch/MonarchResolver";
import { LearnPageRedesignTestResult } from "@/monarch/rules/learnPageRedesign/types";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";

type ShortCodeCareerCardsProps = {
  heading?: string;
  title: string;
  variant?: "default" | "male-variant" | "female-variant";
  "cta-text"?: string;
  "cta-href"?: string;
  "hidden-cta"?: boolean;
  // Support lowercase versions from WordPress
  ctatext?: string;
  ctahref?: string;
  hiddencta?: boolean;
  // hidden cta
  "class-name"?: string;
  classname?: string;
  industry?:
    | "dataScience"
    | "businessManagement"
    | "liberalArt"
    | "computerScience"
    | "socialSciences";
  location?: string;
  children?: JSX.Element;
  isSideRail?: boolean;
  "is-side-rail"?: boolean;
};

export function ShortCodeCareerCards({
  heading,
  title,
  variant = "default",
  "cta-text": ctaText,
  "cta-href": ctaHref,
  "hidden-cta": hiddenCta,
  ctatext,
  ctahref,
  hiddencta,
  "class-name": className,
  classname,
  location = "detail-page",
  children,
  industry = "dataScience",
  isSideRail = false,
  "is-side-rail": isSideRailKebab,
}: ShortCodeCareerCardsProps) {
  const stats: CareerStat[] = [];
  const skills: string[] = [];

  Children.toArray(children).forEach((child: ReactNode) => {
    if (isValidElement(child)) {
      const element = child as React.ReactElement<{
        label?: string;
        value?: string;
        icon?: IconTypes;
        skill?: string;
      }>;

      // Handle stat items
      if (element.props.label && element.props.value && element.props.icon) {
        stats.push({
          label: element.props.label,
          value: element.props.value,
          icon: element.props.icon,
        });
      }

      // Handle skill items
      if (element.props.skill) {
        skills.push(element.props.skill);
      }
    }
  });

  // Use kebab-case version if available, otherwise fall back to lowercase version
  const finalCtaText = ctaText || ctatext;
  const finalCtaHref = ctaHref || ctahref;
  const finalClassName = className || classname;
  const finalHiddenCta = hiddenCta || hiddencta || false;
  const finalIsSideRail = isSideRail || isSideRailKebab || false;
  return (
    <FeatureFlagComponentResolverClient
      flagKey="NEXT_PUBLIC_FEATURE_LEARN_PAGE_REDESIGN_TRAFFIC"
      variants={{
        legacy: null,
        test: (
          <MonarchResolver<LearnPageRedesignTestResult>
            experimentKey="useLearnPageRedesign"
            variants={{
              control: null,
              test: (
                <MonarchDataConsumer<LearnPageRedesignTestResult>
                  experimentKey="useLearnPageRedesign"
                  render={(testResult) => {
                    // Check if career cards are enabled in the experiment
                    if (
                      !testResult?.data?.careerCards ||
                      !testResult.data.careerCards.enabled
                    ) {
                      return null;
                    }
                    return (
                      <CareerCards
                        variant={variant}
                        heading={heading}
                        title={title}
                        stats={stats}
                        skills={skills}
                        ctaText={finalCtaText}
                        ctaHref={finalCtaHref}
                        className={finalClassName}
                        hiddenCta={finalHiddenCta}
                        industry={industry}
                        location={location}
                        isSideRail={finalIsSideRail}
                      />
                    );
                  }}
                />
              ),
            }}
            Loader={null}
            fallbackVariant="control"
          />
        ),
      }}
    />
  );
}

// These are placeholder components that serve as data carriers for the parent component
// They don't render anything - the parent extracts their props
export function ShortCodeCareerCardsStat() {
  return null;
}

export function ShortCodeCareerCardsSkill() {
  return null;
}
